import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import ReactGA from "react-ga4"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useNavigate } from "react-router-dom"

import * as Yup from "yup"
import { Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import {
  Button,
  InputChips,
  NotificationUtils,
  Select,
  SingleDayPicker,
  Snackbar,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik, useFormikContext } from "formik"
import { t } from "i18next"
import moment from "moment"

import { InfoBlock } from "../../../components/containers/InfoBlock"
import { SidAPI } from "../../../services/SidAPI"
import type { OrganizationInvitationBulkRequest } from "../../../types/generated/authentication/OrganizationInvitationBulkRequest"
import type { OrganizationInvitationBulkResponse } from "../../../types/generated/authentication/OrganizationInvitationBulkResponse"
import type { Role } from "../../../types/generated/authentication/Role"
import type {
  EmailCardFormikValues,
  EmailCardProps,
  InviteMembersContainerProps,
  InviteMembersFormikValues,
  InviteMembersProps,
} from "../interfaces"

import styles from "./InviteMembers.module.scss"

/**
 * Component that renders a single email card with role selection
 *
 * @param {string} email - Email address to display
 * @param {number} index - Index of the email in the users array
 * @returns {ReactElement} Card displaying email and role selector
 */
const EmailCard = (props: Readonly<EmailCardProps>): ReactElement => {
  const { email, index } = props

  const { t } = useTranslation("auth")

  const { values, setFieldValue, handleBlur } = useFormikContext<EmailCardFormikValues>()

  const options = [
    {
      label: t("inviteMembers.role.viewer"),
      value: 0,
    },
    {
      label: t("inviteMembers.role.owner"),
      value: 1,
    },
  ]

  return (
    <Grid container xs={12} justifyContent="space-between" wrap="nowrap" gap={1}>
      <Grid item className={styles.emailCard}>
        <InfoBlock text={email} textTransform="initial" type="default" />
      </Grid>

      <Grid item>
        <Select
          id="position"
          type="text"
          width={110}
          handleBlur={handleBlur}
          value={values.users[index]?.role}
          handleChange={(e: InputChangeEvent) => setFieldValue(`users.[${index}].role`, e.target.label)}
          optionsWithValues={options}
        />
      </Grid>
    </Grid>
  )
}

/**
 * Component that renders the invite members form with email input, role selection and expiry date
 *
 * @param {boolean} isInviteUserError - Indicates if invitation attempt failed
 * @param {AxiosError} inviteUserError - Error details if invitation failed
 * @param {moment.Moment} selectedDate - Selected expiry date for invitations
 * @param {(date: moment.Moment) => void} setSelectedDate - Handler to update selected date
 * @returns {ReactElement} Invite members form with validation states
 */
export function InviteMembers(props: Readonly<InviteMembersProps>): ReactElement {
  const { isInviteUserError, inviteUserError, selectedDate, setSelectedDate } = props

  const { t } = useTranslation("auth")

  const { values, isSubmitting, isValid, dirty, setFieldValue, errors, submitForm, validateForm } =
    useFormikContext<InviteMembersFormikValues>()

  const navigate = useNavigate()

  // Disable accessing past dates in date picker
  const disablePast = (date: moment.Moment): boolean => {
    return moment() < date
  }

  // Handle date selection in the picker
  const handleDateChange = (startDate: moment.Moment | null): void => {
    setSelectedDate(startDate as moment.Moment)
  }

  const handleEmailsChange = (emails: string[]): void => {
    setFieldValue(
      "users",
      emails.map((email) => {
        return {
          email: email,
          role: values.users.find((user) => user.email === email)?.role ?? t("inviteMembers.role.viewer"),
        }
      }),
    )
    validateForm()
  }

  return (
    <Grid item container className={styles.mainContainer}>
      {/* Network error */}
      {isInviteUserError && inviteUserError?.message === "Network Error" && (
        <Snackbar fullWidth severity="error" description={t("inviteMembers.networkError")} />
      )}

      <Grid container className={styles.formContainer}>
        <Grid item container xs={12} justifyContent="start">
          <Typography variant="h1-bold">{t("inviteMembers.title")}</Typography>
        </Grid>

        <Grid item container xs={12} direction="column" gap={1}>
          <Typography variant="label-bold">{t("inviteMembers.email.label")}</Typography>

          <InputChips
            value={values.lastInput}
            handleChange={(e: InputChangeEvent) => {
              setFieldValue("lastInput", e.target.value)
              validateForm()
            }}
            handleBlur={() => {
              if (!!values.lastInput && Yup.string().email().isValidSync(values.lastInput)) {
                setFieldValue("users", [
                  ...values.users,
                  { email: values.lastInput, role: t("inviteMembers.role.viewer") },
                ])
                setFieldValue("lastInput", "")
              }
              validateForm()
            }}
            setEnteredChipsValue={handleEmailsChange}
            enteredChipsValue={values.users.map((user) => user.email)}
            email
            fullWidth
            placeholder={t("inviteMembers.email.placeholder")}
            description={(errors.users as string) ?? t("inviteMembers.email.helpText")}
          />
        </Grid>

        {Object.keys(values.users).length > 0 && (
          <Grid item container xs={12} maxHeight={"12.5rem"} overflow="auto" gap={1}>
            {values.users.map((user, index) => (
              <EmailCard key={user.email} email={user.email} index={index} />
            ))}
          </Grid>
        )}

        <Grid item container xs={12} direction="column" gap={1}>
          <Typography
            variant="label-bold"
            tooltip={
              <Fragment>
                <b>{t("inviteMembers.expiryDateTooltip")}</b>
              </Fragment>
            }
          >
            {t("inviteMembers.expiryDate")}
          </Typography>

          <SingleDayPicker date={selectedDate} onDateChange={handleDateChange} isOutsideRange={disablePast} />
        </Grid>

        <Grid container item xs={12} gap={1.5}>
          <Button
            fullWidth
            id="invite-button"
            variant="primary"
            type="submit"
            onClick={() => submitForm()}
            disabled={isSubmitting || !(isValid || dirty) || !validationSchema.isValidSync(values)}
            isLoading={isSubmitting}
          >
            {t("inviteMembers.invite")}
          </Button>

          <Button fullWidth id="skip" variant="secondary" onClick={() => navigate("/")}>
            {t("inviteMembers.skip")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = Yup.object().shape({
  users: Yup.array()
    .of(
      Yup.object().shape({
        email: Yup.string()
          .email()
          .required(t("inviteMembers.email.required", { ns: "auth" })),
        role: Yup.string()
          .oneOf([t("inviteMembers.role.viewer", { ns: "auth" }), t("inviteMembers.role.owner", { ns: "auth" })])
          .required(),
      }),
    )
    .min(1, t("inviteMembers.validation.atLeastOneUser", { ns: "auth" })),
})

/**
 * Container component that manages the invite members state and form submission logic.
 * Handles fetching roles and sending invitations to both viewers and owners.
 *
 * @param {Object} org - Organization details including uid
 * @returns {ReactElement} The invite members form wrapped in Formik provider
 */
export function InviteMembersContainer(props: Readonly<InviteMembersContainerProps>): ReactElement {
  const { org } = props

  const navigate = useNavigate()

  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment().add(7, "days"))

  // Fetching organization roles
  const { isLoading: isRolesLoading, data: roles } = useQuery<AxiosResponse<Role[]>, AxiosError>(
    ["roles", org?.uid],
    () => SidAPI.fetchRoles(org?.uid as string),
    {
      refetchOnMount: false,
      enabled: !!org?.uid,
    },
  )

  // Getting the id of the OWNER and VIEWER/USER role from the query result
  const viewerRoleId = roles?.data.find((element) => element.name === "USER")?.uid
  const ownerRoleId = roles?.data.find((element) => element.name === "OWNER")?.uid

  const inviteUserMutation = useMutation<
    AxiosResponse<OrganizationInvitationBulkResponse>,
    AxiosError,
    OrganizationInvitationBulkRequest
  >(
    (props: OrganizationInvitationBulkRequest) => {
      return SidAPI.inviteUsers({
        orgId: org?.uid as string,
        emails: props.emails,
        expiry_date: props.expiry_date,
        role: props.role,
        groups: props.groups,
      })
    },
    {
      onSuccess: (response) => {
        // This condition indicates that some emails failed to get sent
        if (response.data.fail.length > 0) {
          const roleType = JSON.parse(response.config.data).role === ownerRoleId ? "owners" : "viewers"
          NotificationUtils.toast(t("inviteMembers.notifications.invitationsPartialFailure", { role: roleType }), {
            severity: "warning",
          })
        } else {
          // All emails were sent
          const roleType = JSON.parse(response.config.data).role === ownerRoleId ? "owners" : "viewers"
          NotificationUtils.toast(
            response.data.success.length > 1
              ? t("inviteMembers.notifications.invitationsSuccessMultiple", { role: roleType })
              : t("inviteMembers.notifications.invitationsSuccessSingle", { role: roleType }),
            {
              severity: "success",
            },
          )
        }

        navigate("/")
      },
      onError: () => {
        NotificationUtils.toast(t("inviteMembers.notifications.invitationsError"), {
          severity: "error",
        })
      },
    },
  )

  // form control
  const formik = useFormik({
    initialValues: {
      users: [] as {
        email: string
        role: string
      }[],
      lastInput: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Group users by their role (Owner/Viewer) using Object.groupBy
      const emails = Object.groupBy(values.users, ({ role }) => role)

      // Proceed only if there are users to invite and roles are loaded
      if (formik.values.users.length > 0 && roles && !isRolesLoading) {
        // Handle Viewer invitations
        const viewerEmails = emails[t("inviteMembers.role.viewer")]
        if (!!viewerEmails && viewerEmails.length > 0 && viewerRoleId) {
          await inviteUserMutation.mutateAsync({
            emails: viewerEmails.map((viewer) => viewer.email),
            role: viewerRoleId,
            expiry_date: selectedDate?.toISOString(),
          })
        }

        // Handle Owner invitations
        const ownerEmails = emails[t("inviteMembers.role.owner")]
        if (!!ownerEmails && ownerEmails.length > 0 && ownerRoleId) {
          await inviteUserMutation.mutateAsync({
            emails: ownerEmails.map((owner) => owner.email),
            role: ownerRoleId,
            expiry_date: selectedDate?.toISOString(),
          })
        }

        // Track successful invitation event in Google Analytics
        ReactGA.event({
          category: "Users",
          action: "Invited users to organization",
        })
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <InviteMembers
        isInviteUserError={inviteUserMutation.isError}
        inviteUserError={inviteUserMutation.error}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
    </FormikProvider>
  )
}
